/* eslint-disable no-debugger, no-console, padding-line-between-statements */
import * as fs from "fs";
import env from "./utils/env.config";
import { loadGuestSnapshot } from "./utils/guestSnapshot";

const apiCall = async (url: string, APIVersion: string = env.apiV1) => {
  const res = await fetch(`${env.baseUrl}${url}`, {
    headers: {
      version: APIVersion,
    },
  });

  return res;
};

export const waitForMs = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * `res.json()`, except that 204 means there is nothing to parse.
 *
 * `/restaurant/social-media/<id>` answers 204 with a zero-byte body for every
 * restaurant with no social-media row -- eight of twelve eg restaurants
 * sampled -- and JSON.parse("") throws "Unexpected end of JSON input".
 * getRestaurantStaticProps already catches that and carries on with no social
 * media, so nothing was broken; it just logged a stack trace per page per
 * locale. One cariboucoffeenext build produced 1,263 of them.
 *
 * A 204 body is empty by definition, so returning null is what the status
 * means rather than a guess about it. Callers see exactly what they saw
 * before: getRestaurantStaticProps turns it into `{}` via `|| {}`, and the
 * item list treats a non-array as a miss and retries. #4166
 */
const parseJson = (res: Response): Promise<any> =>
  res.status === 204 ? Promise.resolve(null) : res.json();

/** `/restaurant/<id>`, the one call whose body IS the page. */
const RESTAURANT_URL = /^\/restaurant\/[0-9]*$/;

/**
 * The body of a usable answer, or a throw.
 *
 * `fetch` does NOT reject on 4xx/5xx, and nothing here checked `res.ok`, so an
 * error body was parsed as though it were data, written into
 * backendCalls.json, and prerendered into every page of the build.
 *
 * munchix shipped exactly that on 2026-09-08: the eg fleet builds every
 * restaurant at once, `/restaurant/853` answered
 *
 *   429 {"message":"Too many requests, please try again in one hour!"}
 *
 * and munchix.prepit.app went live serving that object as `restaurantInfo` --
 * no config, no franchises, `<title>Undefined</title>`, no menu. It stayed
 * that way silently, because the build succeeded. getRestaurantStaticProps
 * deliberately leaves `restaurantInfo` un-caught so a failure fails the build;
 * it never saw a failure to catch.
 *
 * Throwing here is what makes that guarantee real, and it costs no extra
 * request: #230/#234 removed the retry budget from this path because
 * getRestaurantStaticProps runs on every page and /api/revalidate re-renders
 * ~26 routes per locale synchronously, which multiplied into minutes and timed
 * out twelve UI suites. A throw is still exactly one request.
 *
 * A 200 is not automatically usable either: `/restaurant/<id>` without an `id`
 * is the same dead storefront by another route, and the item path has thrown on
 * it since #230. The guard belongs on both paths, not just the one that
 * happened to crash.
 *
 * Callers that can live without the answer already catch it -- a missing
 * social-media row resolves to `{}` and the pixels stay off, exactly as when it
 * 404s.
 */
const usableBody = async (url: string, res: Response): Promise<any> => {
  /**
   * Status BEFORE parsing, and the body read as TEXT on the failure path.
   *
   * The first version of this parsed first, which meant an error page that is
   * not JSON never reached the throw below: `res.json()` rejected with
   *
   *   SyntaxError: Unexpected token '<', "<html>...
   *
   * and that is what the build printed -- no status, no url, nothing naming the
   * call. Observed on billysbelly in run 34480657065: twenty-one restaurants
   * building four at a time put the eg backend over, one item answered with an
   * HTML error page, and `Export encountered an error on /item/[item]` was all
   * there was to go on. The build DID fail, which is the point of the guard, it
   * just failed illegibly -- the exact thing #242 removed the 204 stack traces
   * to stop.
   *
   * Text also cannot throw, so the message survives a truncated or empty body.
   */
  if (!res.ok) {
    const text = await res.text().catch(() => "<unreadable body>");

    throw new Error(
      `[apiCache] ${url} answered HTTP ${res.status}: ${text
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 200)}`
    );
  }

  const body = await parseJson(res);

  if (RESTAURANT_URL.test(url) && !body?.id) {
    throw new Error(
      `[apiCache] ${url} answered without an id, refusing to prerender against it: ${JSON.stringify(
        body
      ).slice(0, 200)}`
    );
  }

  return body;
};

/**
 * How long a runtime `/restaurant/<id>` answer may be reused inside one Lambda
 * container.
 *
 * The build writes `backendCalls.json` and every page reads the menu out of it.
 * The SERVER has no such file -- "backendCalls.json NOT EXIST" is the single
 * most common line in the guest Lambda's log -- so every render fetched
 * `/restaurant/<id>` live, and /api/revalidate renders each path in every
 * locale.
 *
 * That is self-defeating at sync scale. Core's rate limiter allows 40 requests
 * a minute for `/restaurant/` (src/utils/constants.ts), x2 per IP and x3 per
 * user-agent; a Lambda fleet behind a handful of NAT addresses with one
 * user-agent is a single bucket. sushiandco's sync on 2026-09-12 19:00 UTC
 * asked for ~750 pages, so ~1500 identical calls, and the backend answered
 *
 *   429 {"message":"Too many requests, please try again in one hour!"}
 *
 * which threw out of getStaticProps, took the render with it, and made
 * `res.revalidate()` report "Invalid response 500". 490 of those in one hour,
 * and every one of them left the page on its old version -- the same stale
 * checkout that `ReqItemsContainsOutDatedItems` rejects. 74 of sushiandco's
 * 381 item pages were still stale the next day, on a freshly deployed build.
 *
 * 60s is chosen against the failure it prevents rather than against freshness:
 * the burst is what has to collapse, and a config change does not wait for the
 * TTL because `/api/revalidate?set=config` clears this outright (see
 * clearRestaurantCache). What a guest can see is a restaurant config up to a
 * minute old -- against, today, a page that never regenerates at all.
 */
const DEFAULT_RESTAURANT_TTL_MS = 60_000;

/**
 * Read per call, not at import, and settable to 0 to switch the cache off.
 *
 * CI needs it off. matrix-ui-tests.yml deletes backendCalls.json before
 * `next start` precisely so every render fetches live -- "a menu mutated
 * mid-suite would never reach the page" -- and an in-process memo re-creates
 * the problem the deletion exists to avoid. It runs under `next start`, so
 * NODE_ENV is "production" there and a development-only bypass does not reach
 * it: the switch has to be explicit, and it lives next to that `rm -f`.
 *
 * `next dev` defaults to off for the same reason, and because there is nothing
 * to protect: the burst this cache absorbs is /api/revalidate re-rendering
 * hundreds of pages, and dev has no ISR.
 */
const restaurantTtlMs = (): number => {
  const configured = Number(process.env.RESTAURANT_CACHE_TTL_MS);

  if (Number.isFinite(configured) && configured >= 0) {
    return configured;
  }

  return process.env.NODE_ENV === "development" ? 0 : DEFAULT_RESTAURANT_TTL_MS;
};

/**
 * Keyed by url, holding the PROMISE rather than the body.
 *
 * Holding the promise is what deduplicates concurrent renders: the guest
 * Lambda runs many at once (a dozen `START RequestId` inside the same second
 * during a sync), and a body-only cache would let every one of them miss and
 * fire its own request before the first came back.
 */
const restaurantCache = new Map<string, { at: number; body: Promise<any> }>();

/**
 * Dropped whenever core asks for the config page set.
 *
 * `?set=config` means "the restaurant config changed, rebuild every page that
 * bakes it in" -- serving those rebuilds from a cache populated before the
 * change is the one case where the TTL above would be wrong rather than merely
 * stale. Item revalidations (`?path=/item/...`) deliberately do NOT clear it:
 * the item body comes from `/items/item/<id>`, which is never cached here, and
 * those are the calls that arrive in the hundreds.
 */
export const clearRestaurantCache = (): void => {
  restaurantCache.clear();
};

/**
 * Only the config-shaped call, never an item: item versions must be exact.
 *
 * Playwright drives config changes straight into the backend and reloads the
 * page expecting to see them, a second apart rather than a minute, so holding
 * `/restaurant/<id>` broke every suite that does it -- featureEGMobileUae
 * ("UAE preferred country"), featureOfflineMode ("noOnlineUrl is enabled",
 * "restaurant has no partner restaurants"), featureCanadaMobile2
 * ("franchise-dropdown"), featureEGGeneric, featureEGMobile, featureEGMobile2,
 * featureDownloadAppPopup. That is what restaurantTtlMs() being 0 in CI is for.
 */
const cacheableAtRuntime = (url: string): boolean =>
  RESTAURANT_URL.test(url) || url.startsWith("/restaurant/social-media/");

const runtimeCall = async (url: string, APIVersion: string): Promise<any> => {
  const ttlMs = restaurantTtlMs();

  if (ttlMs === 0 || !cacheableAtRuntime(url)) {
    return await usableBody(url, await apiCall(url, APIVersion));
  }

  const hit = restaurantCache.get(url);

  if (hit && Date.now() - hit.at < ttlMs) {
    return await hit.body;
  }

  const body = (async () => usableBody(url, await apiCall(url, APIVersion)))();
  const entry = { at: Date.now(), body };

  restaurantCache.set(url, entry);

  try {
    return await body;
  } catch (error) {
    // A failure must not be cached for a minute: the next render should get to
    // try again. usableBody throws on 429 and on 5xx, and both are transient.
    //
    // Only if this entry is still the one on file -- a blind delete could drop
    // a good answer that replaced it while this request was in flight.
    if (restaurantCache.get(url) === entry) {
      restaurantCache.delete(url);
    }

    throw error;
  }
};

const CACHE_FILE = "backendCalls.json";

/**
 * Merge `entries` into the build cache instead of overwriting it.
 *
 * The previous code held the whole object across an `await` and wrote that
 * snapshot back, so two renders resolving at once each wrote a copy taken
 * BEFORE the other's entry existed and the loser's key was erased -- which
 * produced fresh misses later in the same build.
 *
 * Read and write are both synchronous with no await between them, so this
 * cannot interleave with another render.
 */
const persistToBuildCache = (entries: Record<string, any>): void => {
  let current: Record<string, any> = {};

  try {
    if (fs.existsSync(CACHE_FILE)) {
      current = JSON.parse(fs.readFileSync(CACHE_FILE, "utf-8")) ?? {};
    }
  } catch {
    // A corrupt or half-written cache is not worth failing a build over --
    // the entries below are about to be written regardless.
    current = {};
  }

  fs.writeFileSync(
    CACHE_FILE,
    JSON.stringify({ ...current, ...entries }),
    "utf-8"
  );
};

/**
 * In-flight build requests, keyed by url and holding the PROMISE.
 *
 * `next build` renders pages concurrently inside a single process -- `cpus: 1`
 * and `workerThreads: false` in next.config.js limit it to one worker, not to
 * one page at a time. Without this, every render in a batch reads the cache
 * file, misses, and fires its own request before any of them has written:
 * fourteen identical `/restaurant/179` calls inside 130ms on 2026-09-13, each
 * of which also dragged in a full `/items/list` menu pull.
 *
 * Deliberately the same shape as `restaurantCache` above, which has always done
 * this for the RUNTIME path ("Holding the promise is what deduplicates
 * concurrent renders"). The build path simply never had it.
 */
const inFlightBuildCalls = new Map<
  string,
  Promise<{ data: any; allItems: any[] }>
>();

const fetchIntoBuildCache = (
  url: string,
  APIVersion: string
): Promise<{ data: any; allItems: any[] }> => {
  const existing = inFlightBuildCalls.get(url);

  if (existing) {
    return existing;
  }

  const request = (async () => {
    const data = await usableBody(url, await apiCall(url, APIVersion));
    const entries: Record<string, any> = { [url]: data };
    let allItems: any[] = [];

    // `data.id` is guaranteed here: usableBody refuses a restaurant body
    // without one, so there is no longer a second "cannot resolve item"
    // throw for the same condition.
    if (RESTAURANT_URL.test(url)) {
      allItems = await fetchAllItemsPerRestaurant(data.id);
      entries[`${url}/allItems`] = allItems;
    }

    persistToBuildCache(entries);

    return { data, allItems };
  })();

  inFlightBuildCalls.set(url, request);

  // A failure must not be memoised for the rest of the build -- usableBody
  // throws on 429 and 5xx and both are transient, so the next render should get
  // to try again. Identity-checked for the same reason runtimeCall does it.
  request
    .catch(() => undefined)
    .then(() => {
      if (inFlightBuildCalls.get(url) === request) {
        inFlightBuildCalls.delete(url);
      }
    });

  return request;
};

export const fetchCall = async (
  url: string,
  APIVersion: string = env.apiV1,
  itemId: number | undefined = undefined
) => {
  const filePath = CACHE_FILE;
  let result: any = "N/A";
  const exists = await fs.existsSync(filePath);
  if (exists && process.env.NODE_ENV !== "development") {
    const cachedApis = await fs.readFileSync(filePath, "utf-8");
    const obj = await JSON.parse(cachedApis as unknown as string);
    const cachedItems = obj[`${url}/allItems`];
    /**
     * Presence, not truthiness.
     *
     * `/restaurant/social-media/<id>` answers 204 for every restaurant with no
     * social row -- three of the four prod restaurants built on 2026-09-13 --
     * and parseJson turns that into `null`, which JSON.stringify faithfully
     * stores. `obj[url] &&` then read that stored answer as a miss, so the
     * cache recorded the result and refused to believe it: 80 identical
     * requests in one build, one per render, against 14 for the restaurant
     * whose row exists.
     */
    const isCached = Object.prototype.hasOwnProperty.call(obj, url);
    // A cached restaurant whose item list is missing or malformed is not a
    // usable hit -- fetch it again rather than .find on it.
    if (isCached && (!itemId || Array.isArray(cachedItems))) {
      if (itemId) {
        const itemData = cachedItems.find((item: any) => item.id === itemId);
        result = { restaurantData: obj[url], itemData };
      } else {
        result = obj[url];
      }
    } else {
      const { data, allItems } = await fetchIntoBuildCache(url, APIVersion);
      if (itemId) {
        const itemData = allItems.find((item: any) => item.id === itemId);
        result = { restaurantData: data, itemData };
      } else {
        result = data;
      }
    }
  } else {
    /**
     * RUNTIME. No build cache on disk, so this is the Lambda.
     *
     * The snapshot is consulted exactly as the build branch above consults the
     * file, against the same two keys -- that is the whole point of giving it
     * the same shape. Null means "no snapshot", which is every environment
     * that is not the OpenNext Lambda, and the code below it is unchanged
     * from before this existed.
     *
     * (The `console.log(filePath, "NOT EXIST")` that used to sit here is gone:
     * it fired three times per render, 22,170 lines in one day on vasko, on
     * the hot path, and said only that we are not in a build.)
     */
    const snapshot = await loadGuestSnapshot();

    if (itemId) {
      // Presence, not truthiness -- see the note in the build branch above.
      const snapshotItems = snapshot?.[`${url}/allItems`];
      const fromSnapshot = Array.isArray(snapshotItems)
        ? snapshotItems.find((item: any) => item.id === itemId)
        : undefined;

      if (fromSnapshot) {
        result = { itemData: fromSnapshot };
      } else {
        /**
         * Falls back PER ITEM, not to the whole list.
         *
         * A cold container with no snapshot must not block a render on the
         * 6.7MB list (18.2s measured on vasko) inside a 30s budget. One item
         * is 21KB, and /items/list v1 and /items/item/<id> v2 were verified
         * deep-equal ignoring array order, so the two sources agree.
         */
        const restaurantId = url.split("/")[2];
        const itemUrl = `/items/item/${itemId}?restaurantId=${restaurantId}`;
        const itemRes = await apiCall(itemUrl, env.apiV2);
        const itemData = await usableBody(itemUrl, itemRes);
        result = { itemData };
      }
    } else if (
      snapshot &&
      Object.prototype.hasOwnProperty.call(snapshot, url)
    ) {
      result = snapshot[url];
    } else {
      result = await runtimeCall(url, APIVersion);
    }
  }
  return result;
};

const ITEMS_ATTEMPTS = 3;

/**
 * The item list, which must be an array.
 *
 * A build pulls the restaurant's entire menu -- cariboucoffeenext is 11.7MB and
 * ~20s idle -- and the eg fleet builds every restaurant at once. Under that
 * burst this answered with an error body instead of the array and .find on it
 * aborted the whole build on its first item page ("TypeError: s.find is not a
 * function"), taking down 12 of 23 eg deploys in run 34209276902.
 *
 * The retry lives HERE and nowhere else on purpose. This is the only call that
 * crashed, and it is reached only while `next build` populates
 * backendCalls.json -- never from the server, which has no such file. Every
 * other call keeps its single-request behaviour, because /api/revalidate
 * re-renders ~26 routes in every locale synchronously and each of those runs
 * getRestaurantStaticProps: a retry budget on that path multiplies into
 * minutes and timed out twelve UI suites. #4166
 */
const fetchAllItemsPerRestaurant = async (restaurantId: number) => {
  const url = `/items/list?restaurantId=${restaurantId}`;
  let lastFailure = "";

  for (let attempt = 1; attempt <= ITEMS_ATTEMPTS; attempt++) {
    try {
      const res = await apiCall(url);
      const data = await parseJson(res);

      if (Array.isArray(data)) {
        return data;
      }

      lastFailure = `HTTP ${res.status}, ${JSON.stringify(data).slice(0, 200)}`;
    } catch (error: any) {
      lastFailure = error?.message ?? String(error);
    }

    console.log(
      `[apiCache] ${url} attempt ${attempt}/${ITEMS_ATTEMPTS}: ${lastFailure}`
    );

    if (attempt < ITEMS_ATTEMPTS) {
      await waitForMs(1000 * attempt);
    }
  }

  throw new Error(
    `[apiCache] ${url} did not return an array after ${ITEMS_ATTEMPTS} attempts: ${lastFailure}`
  );
};
